// src/frontend/src/store/auth.js
import { defineStore } from 'pinia';
import api from '@/api';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: null,
    refreshToken: null,
    initialized: false
  }),

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    isAdmin: (state) => state.user?.role === 'admin'
  },

  actions: {
    async initialize() {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');

      if (token) {
        this.token = token;
        this.refreshToken = refreshToken;
        api.setAuthToken(token);

        try {
          await this.fetchUser();
        } catch (error) {
          // Token might be expired, try to refresh
          if (refreshToken) {
            try {
              await this.refresh();
            } catch {
              this.clearAuth();
            }
          } else {
            this.clearAuth();
          }
        }
      }

      this.initialized = true;
    },

    async login(email, password) {
      const response = await api.auth.login(email, password);

      this.token = response.token;
      this.refreshToken = response.refreshToken;
      this.user = response.user;

      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);

      api.setAuthToken(response.token);

      return response;
    },

    async refresh() {
      if (!this.refreshToken) {
        throw new Error('No refresh token');
      }

      const response = await api.auth.refresh(this.refreshToken);

      this.token = response.token;
      this.refreshToken = response.refreshToken;

      localStorage.setItem('token', response.token);
      localStorage.setItem('refreshToken', response.refreshToken);

      api.setAuthToken(response.token);

      await this.fetchUser();

      return response;
    },

    async fetchUser() {
      const response = await api.auth.me();
      this.user = response.user;
      return response;
    },

    async logout() {
      try {
        await api.auth.logout();
      } catch {
        // Ignore logout errors
      }

      this.clearAuth();
    },

    async changePassword(currentPassword, newPassword) {
      await api.auth.changePassword(currentPassword, newPassword);
    },

    clearAuth() {
      this.user = null;
      this.token = null;
      this.refreshToken = null;

      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');

      api.setAuthToken(null);
    }
  }
});
